import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApprovalPolicy,
  createDocumentSchema,
  type AccessRuleInput,
  type CreateDocumentInput,
} from '@qualicloud/contracts';
import { addCalendarMonths, computeValidUntil } from '@qualicloud/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AccessRuleEditor } from '@/components/access-rule-editor';
import { ConfigurationWarnings } from '@/components/configuration-warnings';
import {
  Button,
  Card,
  CardHeader,
  DatePicker,
  Field,
  FileDropzone,
  FormError,
  IconAlertTriangle,
  IconInfo,
  Input,
  NumberInput,
  Select,
  Switch,
  Tabs,
  Textarea,
  parseIso,
  toIso,
  useToast,
} from '@/design-system';
import { documentTypesApi, documentsApi, mediaApi, queryKeys, toUserMessage } from '@/lib/api';
import type { DocumentTypeNode } from '@/lib/api/types';
import { formatDateOnly } from '@/lib/format/datetime';
import { APPROVAL_POLICY_DESCRIPTION, APPROVAL_POLICY_LABEL } from '@/lib/i18n';
import { useDirectory } from '@/lib/use-directory';
import { previewValidity } from '@/lib/validity-presentation';
import { PageBody, PageHeader } from '@/shell/app-shell';
import { CadastroRapidoDeTipoDeDocumento } from '@/components/cadastro-rapido';

type FormValues = CreateDocumentInput;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FORMULÁRIO DE DOCUMENTO — criação e nova versão
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Três abas, espelhando o legado (Geral / arquivo / Distribuição) e corrigindo:
 *
 * · O MESMO schema Zod (`createDocumentSchema` de @qualicloud/contracts) valida
 *   aqui e no DTO do NestJS. Divergência entre cliente e servidor é impossível
 *   por construção — não há um segundo conjunto de regras neste arquivo.
 *
 * · VALIDADE: o legado calculava `validade_date` 100% no cliente com
 *   `setMonth()` (que faz overflow: 31/01 + 1 mês = 03/03) e gravava o texto
 *   como veio, sem recalcular (D-VAL-01, D-VAL-02). Aqui a data calculada é
 *   PRÉ-VISUALIZAÇÃO explícita, marcada como tal na tela, usando
 *   `computeValidUntil`/`addCalendarMonths` do domínio — as MESMAS funções do
 *   servidor, que saturam no último dia do mês. O valor gravado é sempre o que
 *   o servidor recalcular.
 *
 * · UPLOAD: validação de extensão e tamanho antes de subir, e o binário vai
 *   direto ao MEDIA (com antivírus e verificação de magic bytes), nunca pelo
 *   CORE.
 *
 * · DISTRIBUIÇÃO: editor de regras nos três alcances com ALLOW/DENY, mais a
 *   MATRIZ DE PERMISSÃO EFETIVA. No legado a regra do MAX() era incompreensível
 *   e não havia nenhuma forma de conferir o resultado antes de salvar.
 */
export function DocumentFormPage({ mode }: { readonly mode: 'create' | 'new-version' }) {
  const params = useParams({ strict: false }) as { documentId?: string };
  const documentId = params.documentId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [tab, setTab] = useState('dados');
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const typesQuery = useQuery({
    queryKey: queryKeys.documentTypes.tree(),
    queryFn: () => documentTypesApi.tree(),
    staleTime: 5 * 60_000,
  });

  const existingQuery = useQuery({
    queryKey: queryKeys.documents.detail(documentId ?? ''),
    queryFn: () => documentsApi.detail(documentId as string),
    enabled: mode === 'new-version' && Boolean(documentId),
  });

  // Colaboradores, departamentos e o VÍNCULO entre eles. O vínculo é o que
  // permite à matriz avaliar o alcance DEPARTAMENTO — sem ele, regras de
  // departamento apareciam na tabela e não surtiam efeito na conferência.
  const directory = useDirectory();

  const existing = existingQuery.data;

  /**
   * Valores da NOVA VERSÃO, calculados uma vez por documento carregado.
   *
   * O `values` do react-hook-form dispara `reset` sempre que o objeto recebido
   * difere do anterior. Construído em linha, este objeto trazia
   * `revisionDate: new Date()` — um instante NOVO a cada render —, então nunca
   * era igual ao anterior: o reset re-renderizava, o re-render criava outra
   * data, e o ciclo só parava no limite do React ("Maximum update depth
   * exceeded", visível no console ao abrir /documentos/$id/nova-versao).
   *
   * Preso a `existing`, o objeto — e com ele a data da versão — passa a ser
   * estável enquanto o documento carregado for o mesmo.
   */
  const versionValues = useMemo<FormValues | undefined>(() => {
    if (!existing) return undefined;
    return {
      name: existing.name,
      documentTypeId: existing.documentTypeId,
      description: existing.description,
      changeJustification: '',
      revisionDate: new Date(),
      hasValidity: existing.hasValidity,
      validityMonths: existing.validityMonths,
      autoApprove: existing.autoApprove,
      hasControlledPrintedCopy: existing.hasControlledPrintedCopy,
      approvalPolicy: existing.approvalPolicy,
      quorumSize: existing.quorumSize,
      // A distribuição vigente é RECARREGADA na nova versão. Enviar vazio,
      // como se fazia antes, apagava silenciosamente os destinatários da
      // ciência a cada nova versão do documento.
      distributionUserIds: existing.distribution
        .filter((entry) => entry.userId !== null)
        .map((entry) => entry.userId as string),
      distributionDepartmentIds: existing.distribution
        .filter((entry) => entry.departmentId !== null)
        .map((entry) => entry.departmentId as string),
      requiresAcknowledgement: existing.requiresAcknowledgement,
      accessRules: existing.accessRules.map((rule) => ({
        scope: rule.scope as never,
        effect: rule.effect as never,
        userId: rule.userId,
        departmentId: rule.departmentId,
        flags: rule.flags as never,
      })),
    };
  }, [existing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(createDocumentSchema),
    mode: 'onBlur',
    values: versionValues,
    defaultValues: {
      name: '',
      documentTypeId: '',
      description: '',
      changeJustification: '',
      revisionDate: new Date(),
      hasValidity: false,
      validityMonths: 0,
      autoApprove: false,
      hasControlledPrintedCopy: false,
      approvalPolicy: ApprovalPolicy.ANY_ONE,
      quorumSize: null,
      distributionUserIds: [],
      distributionDepartmentIds: [],
      requiresAcknowledgement: false,
      accessRules: [],
    },
  });

  const watched = form.watch();
  const flatTypes = useMemo(() => flattenTypes(typesQuery.data ?? []), [typesQuery.data]);

  const subjects = directory.subjects;
  const departmentOptions = directory.departments;


  const submit = useMutation({
    mutationFn: async (values: FormValues) => {
      // ── Passo 1: upload ao MEDIA (fora do CORE) ─────────────────────────
      let upload: { uploadId: string; fileName: string } | null = null;
      if (file) {
        setUploadProgress(0);
        upload = await mediaApi.uploadFile({
          file,
          onProgress: setUploadProgress,
        });
        setUploadProgress(null);
      }

      if (mode === 'create') {
        if (!upload) throw new Error('Selecione o arquivo do documento na aba Arquivo.');
        return documentsApi.create({ ...values, upload });
      }

      // Nova versão: `upload` ausente é o que dispara, NO SERVIDOR, a regra
      // "alterar metadado exige novo arquivo" (ged.md §6.2).
      return documentsApi.createVersion(documentId as string, {
        ...values,
        upload: upload ?? null,
      });
    },
    onError: (error) => {
      setUploadProgress(null);
      setSubmitError(toUserMessage(error));
    },
    onSuccess: async (result) => {
      setSubmitError(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.documents.all() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all() });
      toast({
        tone: 'success',
        title: mode === 'create' ? 'Documento criado' : 'Nova versão emitida',
        description:
          'A versão foi registrada e submetida ao fluxo de aprovação configurado para o documento.',
      });
      // As duas rotas devolvem o `DocumentDetail` completo — inclusive na
      // emissão de nova versão, em que `documentId` já é conhecido. O teste
      // `'id' in result` existia porque o tipo declarado mentia sobre a
      // resposta; agora o `id` é sempre o do documento certo.
      void navigate({
        to: '/documentos/$documentId',
        params: { documentId: result.id ?? (documentId as string) },
      });
    },
  });

  const onSubmit = form.handleSubmit(
    (values) => submit.mutate(values),
    (errors) => {
      // Leva o usuário à primeira aba com erro. Sem isso, um erro na aba
      // Distribuição some da tela e o formulário parece simplesmente não enviar.
      if (errors.accessRules) setTab('distribuicao');
      else setTab('dados');
    },
  );

  const tabErrors = {
    dados: Boolean(
      form.formState.errors.name ??
        form.formState.errors.documentTypeId ??
        form.formState.errors.changeJustification ??
        form.formState.errors.revisionDate ??
        form.formState.errors.validityMonths ??
        form.formState.errors.version ??
        form.formState.errors.quorumSize,
    ),
    arquivo: mode === 'create' && !file && form.formState.isSubmitted,
    distribuicao: Boolean(form.formState.errors.accessRules),
  };

  /*
   * O número da aba é o de REGRAS, e só.
   *
   * Somava também os destinatários de ciência, porque eram um conceito
   * separado com campos próprios. Agora a ciência é um check DA regra: somar
   * os dois contaria a mesma decisão duas vezes — uma regra com ciência
   * marcada aparecia como "2".
   */
  const distributionTabCount = watched.accessRules?.length ?? 0;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Lista Mestra', to: '/documentos' },
          ...(existing
            ? [
                {
                  label: existing.name,
                  to: '/documentos/$documentId',
                  params: { documentId: existing.id },
                },
              ]
            : []),
          { label: mode === 'create' ? 'Novo documento' : 'Nova versão' },
        ]}
        title={mode === 'create' ? 'Novo documento' : `Nova versão — ${existing?.name ?? ''}`}
        description={
          mode === 'create'
            ? 'Cadastre o documento, envie o arquivo e defina quem terá acesso. A versão inicial entra no fluxo de aprovação configurado.'
            : 'Emita uma nova versão do documento. Alterar os dados exige anexar um novo arquivo — a regra é verificada pelo servidor contra o estado gravado.'
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => void navigate({ to: '/documentos' })}>
              Cancelar
            </Button>
            <Button variant="primary" loading={submit.isPending} onClick={() => void onSubmit()}>
              {mode === 'create' ? 'Criar documento' : 'Emitir versão'}
            </Button>
          </>
        }
      />

      <PageBody>
        {submitError ? (
          <FormError title="A operação não foi concluída" className="mb-4">
            {submitError}
          </FormError>
        ) : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit();
          }}
          noValidate
        >
          <Tabs
            ariaLabel="Seções do formulário de documento"
            value={tab}
            onValueChange={setTab}
            tabs={[
              {
                value: 'dados',
                label: 'Dados',
                hasError: tabErrors.dados,
                content: (
                  <DataSection
                    form={form}
                    types={flatTypes}
                    watched={watched}
                    isNewVersion={mode === 'new-version'}
                  />
                ),
              },
              {
                value: 'arquivo',
                label: 'Arquivo',
                hasError: tabErrors.arquivo,
                content: (
                  <FileSection
                    file={file}
                    onFileChange={setFile}
                    progress={uploadProgress}
                    required={mode === 'create'}
                    isNewVersion={mode === 'new-version'}
                    currentChecksum={existing?.currentVersion?.fileChecksum ?? null}
                  />
                ),
              },
              {
                value: 'distribuicao',
                label: 'Distribuição',
                count: distributionTabCount,
                hasError: tabErrors.distribuicao,
                content: (
                  <div className="flex flex-col gap-5">
                    <Controller
                      control={form.control}
                      name="accessRules"
                      render={({ field, fieldState }) => (
                        <AccessRuleEditor
                          rules={field.value as AccessRuleInput[]}
                          onChange={field.onChange}
                          /*
                           * A distribuição controlada deixou de ser uma seção
                           * própria: virou um check DA REGRA que concede a
                           * leitura. Estes três adereços são o que restou dela
                           * — os mesmos campos que o servidor sempre recebeu.
                           */
                          distributionUserIds={watched.distributionUserIds ?? []}
                          distributionDepartmentIds={watched.distributionDepartmentIds ?? []}
                          onDistributionChange={(userIds, departmentIds) => {
                            form.setValue('distributionUserIds', userIds, { shouldDirty: true });
                            form.setValue('distributionDepartmentIds', departmentIds, {
                              shouldDirty: true,
                            });
                            /*
                             * A exigência do DOCUMENTO é derivada: existe
                             * destinatário, existe exigência. Era um
                             * interruptor à parte, e a incoerência "exigência
                             * ligada sem ninguém para alcançar" precisava de um
                             * aviso próprio. Agora ela não tem como acontecer.
                             */
                            form.setValue(
                              'requiresAcknowledgement',
                              userIds.length > 0 || departmentIds.length > 0,
                              { shouldDirty: true },
                            );
                          }}
                          users={subjects.map((subject) => ({
                            id: subject.userId,
                            name: subject.displayName,
                            hint: subject.jobTitle,
                          }))}
                          departments={departmentOptions}
                          error={fieldState.error?.message}
                        />
                      )}
                    />

                    <ConfigurationWarnings
                      rules={(watched.accessRules ?? []) as AccessRuleInput[]}
                      subjects={subjects}
                      approvalPolicy={watched.approvalPolicy}
                      quorumSize={watched.quorumSize}
                    />
                  </div>
                ),
              },
            ]}
          />
        </form>
      </PageBody>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Aba Dados
// ─────────────────────────────────────────────────────────────────────────────

function DataSection({
  form,
  types,
  watched,
  isNewVersion,
}: {
  readonly form: ReturnType<typeof useForm<FormValues>>;
  readonly types: ReadonlyArray<{ readonly id: string; readonly path: string }>;
  readonly watched: FormValues;
  readonly isNewVersion: boolean;
}) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader headingLevel={3} title="Identificação" />
        <div className="mt-4 flex flex-col gap-4">
          <Field
            label="Nome do documento"
            required
            error={form.formState.errors.name?.message}
            description="Único na organização, sem diferenciar maiúsculas de minúsculas."
          >
            <Input {...form.register('name')} maxLength={255} />
          </Field>

          <Controller
            control={form.control}
            name="documentTypeId"
            render={({ field, fieldState }) => (
              <Field
                label="Tipo de documento"
                required
                error={fieldState.error?.message}
                description="A hierarquia é resolvida por identificador, não por nome."
              >
                <Select
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                  criar={{
                    rotulo: 'Novo tipo de documento',
                    dialogo: (props) => <CadastroRapidoDeTipoDeDocumento {...props} />,
                  }}
                  options={types.map((type) => ({ value: type.id, label: type.path }))}
                  placeholder="Selecione o tipo…"
                />
              </Field>
            )}
          />

          {/*
            NUMERAÇÃO MANUAL — ged.md §6.4f, item 5 do checklist §24.
            A regra "a versão digitada tem precedência sobre o incremento
            automático" existe no domínio (`computeNextVersion`) desde sempre;
            faltava o campo, e sem ele a regra era inalcançável pelo usuário.
            Em branco, vale o cálculo do servidor: ramo A `(n+1).0` quando a
            versão anterior está aprovada, obsoleta ou vencida; ramo B
            `(n+1).<revisão anterior>` nos demais casos.
          */}
          <Field
            label="Número da versão"
            error={form.formState.errors.version?.message}
            description={
              isNewVersion
                ? 'Deixe em branco para o servidor calcular a partir da versão vigente. Preencha apenas se houver política interna de numeração: o número digitado tem precedência e não pode regredir.'
                : 'Deixe em branco para começar em 1.0. O formato é maior.menor, por exemplo 2.1.'
            }
          >
            <Input
              {...form.register('version', {
                // Campo em branco precisa virar `undefined`, não `''`: o schema
                // é `.optional()` sobre um regex que a string vazia não
                // satisfaz, então enviar `''` reprovaria a validação de quem
                // simplesmente não quis numerar à mão.
                setValueAs: (value: unknown) =>
                  typeof value === 'string' && value.trim() === '' ? undefined : value,
              })}
              inputMode="decimal"
              placeholder={isNewVersion ? 'Calculado pelo servidor' : '1.0'}
              className="max-w-[10rem]"
            />
          </Field>

          <Field label="Descrição" error={form.formState.errors.description?.message}>
            <Textarea {...form.register('description')} rows={3} />
          </Field>

          <Field
            label="Justificativa desta versão"
            required
            error={form.formState.errors.changeJustification?.message}
            description="Explique o que mudou e por quê. O texto integra o histórico e é lido em auditoria."
          >
            <Textarea
              {...form.register('changeJustification')}
              rows={3}
              maxLength={4000}
              showCounter
              value={watched.changeJustification}
            />
          </Field>
        </div>
      </Card>

      <div className="flex flex-col gap-5">
        <Card>
          <CardHeader headingLevel={3} title="Validade" />
          <div className="mt-4 flex flex-col gap-4">
            <Controller
              control={form.control}
              name="revisionDate"
              render={({ field, fieldState }) => (
                <Field label="Data da versão" required error={fieldState.error?.message}>
                  <DatePicker
                    value={field.value instanceof Date ? toIso(field.value) : ''}
                    onValueChange={(iso) => field.onChange(parseIso(iso) ?? new Date())}
                    className="max-w-[14rem]"
                  />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="hasValidity"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    // Coerência exigida pelo schema: sem validade → zero meses.
                    if (!checked) form.setValue('validityMonths', 0);
                  }}
                  stateLabels={{ on: 'Com validade', off: 'Sem validade' }}
                  label="O documento tem prazo de validade"
                  description="Documentos sem validade nunca vencem: zero meses significa sem prazo."
                />
              )}
            />

            {watched.hasValidity ? (
              <Field
                label="Validade (meses)"
                required
                error={form.formState.errors.validityMonths?.message}
              >
                <NumberInput
                  {...form.register('validityMonths')}
                  min={1}
                  max={600}
                  className="w-28"
                />
              </Field>
            ) : null}

            <ValidityPreview
              revisionDate={watched.revisionDate}
              validityMonths={Number(watched.validityMonths) || 0}
              hasValidity={watched.hasValidity}
            />
          </div>
        </Card>

        <Card>
          <CardHeader headingLevel={3} title="Aprovação e controle" />
          <div className="mt-4 flex flex-col gap-4">
            <Controller
              control={form.control}
              name="approvalPolicy"
              render={({ field, fieldState }) => (
                <Field label="Política de aprovação" required error={fieldState.error?.message}>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    options={Object.values(ApprovalPolicy).map((policy) => ({
                      value: policy,
                      label: APPROVAL_POLICY_LABEL[policy],
                      description: APPROVAL_POLICY_DESCRIPTION[policy],
                    }))}
                  />
                </Field>
              )}
            />

            {watched.approvalPolicy === ApprovalPolicy.QUORUM ? (
              <Field
                label="Tamanho do quórum"
                required
                error={form.formState.errors.quorumSize?.message}
                description="Número mínimo de aprovações. A matriz da aba Distribuição avisa se não houver aprovadores suficientes."
              >
                <NumberInput {...form.register('quorumSize')} min={1} max={50} className="w-24" />
              </Field>
            ) : null}

            <Controller
              control={form.control}
              name="autoApprove"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  stateLabels={{ on: 'Ativada', off: 'Desativada' }}
                  label="Aprovação automática"
                  description="A versão é aprovada no ato da criação, sem passar pela fila. Mesmo automática, a aprovação é assinada eletronicamente e exige a permissão correspondente."
                />
              )}
            />

            <Controller
              control={form.control}
              name="hasControlledPrintedCopy"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  stateLabels={{ on: 'Sim', off: 'Não' }}
                  label="Existe cópia impressa controlada"
                  description="Sinaliza que há cópias físicas distribuídas que precisam ser recolhidas quando o documento for substituído."
                />
              )}
            />

            {isNewVersion ? (
              <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
                <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  A revisão nunca é incrementada automaticamente. Para mudá-la, informe o número
                  completo em &ldquo;Número da versão&rdquo;.
                </span>
              </p>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * PRÉ-VISUALIZAÇÃO da data de vencimento.
 *
 * O cálculo usa `computeValidUntil`/`addCalendarMonths` de `@qualicloud/domain`
 * — as MESMAS funções do servidor, que saturam no último dia do mês. Ainda
 * assim o texto deixa explícito que o valor final é recalculado no servidor
 * (D-VAL-01): o legado calculava no cliente e gravava o que viesse, então
 * qualquer relógio errado ou fuso diferente produzia data divergente.
 */
function ValidityPreview({
  revisionDate,
  validityMonths,
  hasValidity,
}: {
  readonly revisionDate: Date | string;
  readonly validityMonths: number;
  readonly hasValidity: boolean;
}) {
  const base = revisionDate instanceof Date ? revisionDate : new Date(revisionDate);

  if (!hasValidity || validityMonths < 1 || Number.isNaN(base.getTime())) {
    return (
      <p className="rounded-md border border-border bg-surface-sunken px-3 py-2.5 text-sm text-content-muted">
        Este documento não terá prazo de validade e não aparecerá nos filtros de vencimento.
      </p>
    );
  }

  const utcBase = new Date(
    Date.UTC(base.getFullYear(), base.getMonth(), base.getDate()),
  );
  const validUntil = computeValidUntil({ revisionDate: utcBase, validityMonths });
  const preview = previewValidity({ validUntil, validityMonths });

  const saturated =
    validUntil !== null &&
    addCalendarMonths(utcBase, validityMonths).getUTCDate() !== utcBase.getUTCDate();

  return (
    <div className="rounded-md border border-info bg-info-subtle px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-info-content">
        <IconInfo size={13} aria-hidden="true" />
        Pré-visualização
      </p>
      <p className="mt-1 text-sm text-info-content">
        Com esta data e {validityMonths} {validityMonths === 1 ? 'mês' : 'meses'}, o vencimento
        seria{' '}
        <strong className="font-semibold qc-tabular">
          {validUntil ? formatDateOnly(toIso(new Date(validUntil))) : '—'}
        </strong>{' '}
        ({preview.label.toLowerCase()}).
      </p>
      {saturated ? (
        <p className="mt-1 flex items-start gap-1.5 text-xs text-info-content">
          <IconAlertTriangle size={12} aria-hidden="true" className="mt-0.5 shrink-0" />
          O dia foi ajustado para o último dia do mês de destino, porque o mês resultante é mais
          curto.
        </p>
      ) : null}
      <p className="mt-1.5 text-xs text-info-content opacity-90">
        Este valor é apenas uma prévia. O <strong className="font-semibold">servidor recalcula</strong> a
        data de vencimento ao gravar, e é esse resultado que passa a valer.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Aba Arquivo
// ─────────────────────────────────────────────────────────────────────────────

function FileSection({
  file,
  onFileChange,
  progress,
  required,
  isNewVersion,
  currentChecksum,
}: {
  readonly file: File | null;
  readonly onFileChange: (file: File | null) => void;
  readonly progress: number | null;
  readonly required: boolean;
  readonly isNewVersion: boolean;
  readonly currentChecksum: string | null;
}) {
  return (
    <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
      <Card>
        <CardHeader
          headingLevel={3}
          title={required ? 'Arquivo do documento' : 'Novo arquivo (opcional)'}
          description={
            required
              ? 'O arquivo é obrigatório na criação. Ele é enviado diretamente ao serviço de mídia, que verifica o tipo real do conteúdo e executa antivírus antes de armazenar.'
              : 'Se você alterar qualquer dado do documento, um novo arquivo passa a ser obrigatório. Essa verificação é feita pelo servidor, comparando com o estado gravado.'
          }
        />
        <div className="mt-4">
          <FileDropzone file={file} onFileChange={onFileChange} progress={progress} />
        </div>
      </Card>

      <Card>
        <CardHeader headingLevel={3} title="Como o arquivo é tratado" />
        <ul className="mt-3 flex flex-col gap-2.5 text-sm text-content-muted">
          <li className="flex gap-2">
            <span aria-hidden="true" className="font-semibold text-brand">1.</span>
            <span>
              A extensão e o tamanho são conferidos aqui, no seu navegador, antes de qualquer envio
              — para você não esperar 20 MB subirem em vão.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true" className="font-semibold text-brand">2.</span>
            <span>
              O conteúdo vai para o serviço de mídia, que o guarda numa área de recepção. Ele
              não entra no acervo enquanto não passar pelas conferências abaixo.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true" className="font-semibold text-brand">3.</span>
            <span>
              O servidor recalcula a impressão digital SHA-256, confere o tipo real pelos bytes
              iniciais e executa o antivírus.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true" className="font-semibold text-brand">4.</span>
            <span>
              Aprovado, o arquivo é gravado em armazenamento com bloqueio de escrita, pelo período
              de retenção da organização.
            </span>
          </li>
        </ul>

        {isNewVersion && currentChecksum ? (
          <div className="mt-4 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
              Arquivo da versão vigente
            </p>
            <p className="mt-1 break-all font-mono text-xs text-content">{currentChecksum}</p>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilidades
// ─────────────────────────────────────────────────────────────────────────────

function flattenTypes(
  nodes: readonly DocumentTypeNode[],
): Array<{ id: string; path: string }> {
  const result: Array<{ id: string; path: string }> = [];
  const walk = (list: readonly DocumentTypeNode[]) => {
    for (const node of list) {
      if (node.active) result.push({ id: node.id, path: node.path || node.name });
      if (node.children.length > 0) walk(node.children);
    }
  };
  walk(nodes);
  return result;
}
